import { useState, useEffect } from 'react';

import { showToast } from 'utils/utility';

interface UsePaginatedListParams<T> {
    fetchListData: (
        endPoint: string
    ) => Promise<{
        isSuccess: boolean;
        data: { data: T[] } | undefined;
        status: number;
        message: string;
        error: any;
    }>;
    maxLimit?: number;
    sortingOrder?: 'ASC' | 'DESC';
    sortby?: string;
    path?: string;
    extraQueryParams?: Record<string, string | number | boolean>;
    paginationParams?: Record<string, string | number | boolean>;
    dependencies?: any[];
};

export function usePagination<T>({
    fetchListData,
    maxLimit = 10,
    sortingOrder = 'DESC',
    sortby = 'id',
    extraQueryParams = {},
    path,
    paginationParams = {},
    dependencies = [],
}: UsePaginatedListParams<T>) {

    const [dataList, setDataList] = useState<T[]>([]);
    const [initialLoading, setInitialLoading] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [debouncedSearchText, setDebouncedSearchText] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const [lastId, setLastId] = useState<string | number | null>(null);
    const [firstId, setFirstId] = useState<string | number | null>(null);
    const [disablePrev, setDisablePrev] = useState(true);
    const [disableNext, setDisableNext] = useState(true);
    const [triggerNextPage, setTriggerNextPage] = useState(false);
    const [triggerPreviousPage, setTriggerPreviousPage] = useState(false);


    useEffect(() => {
        goToInitialPage();
    }, [debouncedSearchText, JSON.stringify(extraQueryParams), ...dependencies]);

    useEffect(() => {
        const handler = setTimeout(() => {
            if (searchText.trim().length >= 2 || searchText.trim().length === 0) {
                setDebouncedSearchText(searchText.trim());
            };
        }, 300);

        return () => clearTimeout(handler);
    }, [searchText]);

    // Effect to call goToNextPage after paginationQueryParams update
    useEffect(() => {
        if (triggerNextPage) {
            goToNextPage();
            setTriggerNextPage(false);
        }
    }, [paginationParams, triggerNextPage]);

    // Effect to call goToPreviousPage after paginationQueryParams update
    useEffect(() => {
        if (triggerPreviousPage) {
            goToPreviousPage();
            setTriggerPreviousPage(false);
        };
    }, [paginationParams, triggerPreviousPage]);


    // HANDLER FUNCTIONS
    const refreshPagination = async () => fetchData("INITIAL_PAGE");


    // API CALLS
    const fetchData = async (paginationAction: 'INITIAL_PAGE' | 'NEXT_PAGE' | 'PREVIOUS_PAGE') => {
        setIsLoading(true);

        if (paginationAction === 'INITIAL_PAGE') setInitialLoading(true);

        const extraParamsString = Object.entries(extraQueryParams)
            .filter(([, value]) => value !== undefined && value !== null)
            .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
            .join('&');

        const paginationParamsString = Object.entries(paginationParams)
            .filter(([, value]) => value !== undefined && value !== null)
            .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
            .join('&');

        let paginationPath = `?paginationAction=${paginationAction}&maxLimit=${maxLimit}${lastId || firstId ? `&id=${paginationAction === 'NEXT_PAGE' ? lastId : paginationAction === 'PREVIOUS_PAGE' ? firstId : ''}` : ''
            }&sortingOrder=${sortingOrder}&sortby=${sortby}${debouncedSearchText ? `&searchIt=${debouncedSearchText}` : ''}${extraParamsString ? `&${extraParamsString}` : ''}${paginationParamsString ? `&${paginationParamsString}` : ''}`;

        const requestEndPoint = `${path || ''}${paginationPath}`;

        const response = await fetchListData(requestEndPoint);

        // console.log("response", response.data);

        if (response?.isSuccess && response.data?.data) {

            if (paginationAction === 'INITIAL_PAGE') {
                setDisablePrev(true);
                setDisableNext(response.data.data.length < maxLimit);
                // setDisableNext(response.data.data.length === maxLimit ? false : true);
            } else if (paginationAction === 'NEXT_PAGE') {
                console.log("response.data.data.length on next", response.data.data.length);
                setDisablePrev(false);
                setDisableNext(response.data.data.length < maxLimit  );
                // setDisableNext(response.data.data.length === maxLimit ? false : true);

            } else if (paginationAction === 'PREVIOUS_PAGE') {
                console.log("response.data.data.length on pre", response.data.data.length);
                setDisablePrev(response.data.data.length === 0 ? true : false);
                setDisableNext(false);
            };

            if (response.data.data.length > 0) {
                let data: any = response.data.data;
                setLastId(data[response.data.data.length - 1]?.id);
                setFirstId(data[0]?.id);
                setDataList(response.data.data);
            } else {
                // showToast({
                //     title: "No Data",
                //     message: "No data found",
                //     type: "warning",
                // });
            };
        } else {
            if (response.status === 400 || response.status === 404) {
                setIsLoading(false);
                return;
            };
            showToast({
                type: "error",
                message: "Something went wrong",
            });
        };

        setIsLoading(false);

        if (paginationAction === 'INITIAL_PAGE') setInitialLoading(false);
    };


    // HANDLER FUNCTIONS
    const goToNextPage = () => fetchData('NEXT_PAGE');
    const goToPreviousPage = () => fetchData('PREVIOUS_PAGE');
    const goToInitialPage = () => {
        setDataList([]);
        setLastId(null);
        setFirstId(null);
        fetchData('INITIAL_PAGE');
    };

    return {
        setDataList,
        dataList,
        isLoading,
        searchText,
        setSearchText,
        initialLoading,
        goToNextPage,
        goToPreviousPage,
        goToInitialPage,
        setTriggerNextPage,
        setTriggerPreviousPage,
        disablePrev,
        disableNext,
        refreshPagination
    };

};